import {
  APPROVAL_ROLE_LABELS,
  LEARNING_KIND_LABELS,
  LEARNING_STATUS_LABELS,
  LEARNING_TARGET_LABELS,
  WEEKLY_DELIVERY_STATUS_LABELS,
  type ApprovalRole,
  type LearningProposal,
  type LearningStatus,
  type WeeklyDeliveryStatus,
  type WeeklyMetrics,
  type WeeklyReportRow,
  type WeeklyReportRun,
} from '@lia/contracts';
import { cn } from '../../lib/cn.js';
import { EMPTY } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';

// Weekly consolidated report + supervised learning (setter spec §07.5–§07.10). Presentational only; screens wire the
// API. Unknown values render «—» + «Sin datos» (never 0); scheduled and held appointments are separate columns.

const DELIVERY_TONE: Record<WeeklyDeliveryStatus, StatusTone> = { pending: 'info', sent: 'success', failed: 'danger', not_configured: 'neutral' };
const LEARNING_TONE: Record<LearningStatus, StatusTone> = { proposed: 'info', approved: 'success', rejected: 'neutral', implemented: 'success' };

const nf = new Intl.NumberFormat('es-MX');
const fmtDate = (iso: string | null) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : EMPTY);

function money(amountMinor: string, currency: string): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency, currencyDisplay: 'code' }).format(Number(BigInt(amountMinor)) / 100);
}

function Unknown() {
  return (
    <span className="text-text-secondary">
      {EMPTY} <span className="text-meta">Sin datos</span>
    </span>
  );
}

function Delta({ cur, prev }: { cur: number; prev: number }) {
  const d = cur - prev;
  if (d === 0) return <span className="text-meta text-text-secondary">=</span>;
  return <span className={cn('text-meta font-medium', d > 0 ? 'text-success' : 'text-danger')}>{d > 0 ? `+${nf.format(d)}` : nf.format(d)}</span>;
}

const pct = (r: number | null) => (r === null ? <Unknown /> : `${Math.round(r * 100)} %`);
const dur = (s: number | null) => (s === null ? <Unknown /> : s < 90 ? `${Math.round(s)} s` : `${Math.round(s / 60)} min`);

function unitCost(list: WeeklyMetrics['costPerHeld']) {
  if (!list.length) return <Unknown />;
  return (
    <span className="grid">
      {list.map((c) => (
        <span key={c.currency}>
          {money(c.amountMinor, c.currency)}
          {c.includesEstimates ? <span className="block text-meta text-text-secondary">incluye estimado</span> : null}
        </span>
      ))}
    </span>
  );
}

export function weeklyRowName(r: Pick<WeeklyReportRow, 'level' | 'workspaceName' | 'projectName'>): string {
  if (r.level === 'global') return 'Estateflow (global)';
  if (r.level === 'workspace') return r.workspaceName ?? EMPTY;
  return `${r.workspaceName ?? EMPTY} · ${r.projectName ?? EMPTY}`;
}

export interface WeeklyMetricsTableProps {
  rows: readonly WeeklyReportRow[];
  caption: string;
  className?: string;
}

/** One line per row (global / developer / project): funnel, appointments, speed and economy vs the previous week. */
export function WeeklyMetricsTable({ rows, caption, className }: WeeklyMetricsTableProps) {
  if (!rows.length) return <p className={cn('text-body text-text-secondary', className)}>Sin actividad en el periodo.</p>;
  const th = 'px-2 py-1.5 text-left font-medium';
  const td = 'px-2 py-1.5 align-top ef-tabular';
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full min-w-[960px] text-body" aria-label={caption}>
        <thead>
          <tr className="text-meta text-text-secondary">
            <th scope="col" className={th}>Nombre</th>
            <th scope="col" className={th}>Leads únicos</th>
            <th scope="col" className={th}>Oportunidades</th>
            <th scope="col" className={th}>Calificados</th>
            <th scope="col" className={th}>Agendadas</th>
            <th scope="col" className={th}>Realizadas</th>
            <th scope="col" className={th}>Sin resultado</th>
            <th scope="col" className={th}>Show rate</th>
            <th scope="col" className={th}>Primer intento p50 / p95</th>
            <th scope="col" className={th}>SLA 5 min</th>
            <th scope="col" className={th}>Costo por realizada</th>
            <th scope="col" className={th}>Por revisar</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const c = r.current;
            const p = r.previous;
            return (
              <tr key={`${r.level}:${r.workspaceId ?? ''}:${r.projectId ?? ''}`} className="border-t border-border-subtle">
                <th scope="row" className="px-2 py-1.5 text-left font-medium text-text-primary">{weeklyRowName(r)}</th>
                <td className={td}>
                  {nf.format(c.uniqueLeads)} <Delta cur={c.uniqueLeads} prev={p.uniqueLeads} />
                </td>
                <td className={td}>{nf.format(c.opportunities)}</td>
                <td className={td}>{nf.format(c.qualified)}</td>
                <td className={td}>{nf.format(c.booked)}</td>
                <td className={td}>
                  {nf.format(c.held)} <Delta cur={c.held} prev={p.held} />
                </td>
                <td className={td}>{nf.format(c.pendingOutcome)}</td>
                <td className={td}>{pct(c.showRate)}</td>
                <td className={td}>
                  {dur(c.firstAttemptP50)} / {dur(c.firstAttemptP95)}
                </td>
                <td className={td}>{pct(c.slaRate)}</td>
                <td className={td}>{unitCost(c.costPerHeld)}</td>
                <td className={td}>{nf.format(c.casesToReview)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DeliveryStatusBadge({ channel, status, className }: { channel: string; status: WeeklyDeliveryStatus; className?: string }) {
  return (
    <StatusBadge tone={DELIVERY_TONE[status]} className={className}>
      {channel}: {WEEKLY_DELIVERY_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export interface WeeklyRunListProps {
  runs: readonly WeeklyReportRun[];
  onOpen: (run: WeeklyReportRun) => void;
  className?: string;
}

/** History of generated reports with each delivery's honest state. */
export function WeeklyRunList({ runs, onOpen, className }: WeeklyRunListProps) {
  if (!runs.length) return <p className={cn('text-body text-text-secondary', className)}>Todavía no se ha generado ningún reporte.</p>;
  return (
    <ul className={cn('grid gap-2', className)} aria-label="Historial de reportes">
      {runs.map((r) => (
        <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-border-subtle bg-surface-default p-3">
          <div className="grid gap-1">
            <span className="text-body font-semibold text-text-primary">Semana del {r.weekStart}</span>
            <span className="text-meta text-text-secondary">
              {r.trigger === 'scheduled' ? 'Programado' : `Manual${r.requestedByName ? ` · ${r.requestedByName}` : ''}`} · {fmtDate(r.createdAt)}
            </span>
            <span className="flex flex-wrap gap-2">
              <StatusBadge tone={r.status === 'generated' ? 'success' : r.status === 'failed' ? 'danger' : 'info'}>{r.status === 'generated' ? 'Generado' : r.status === 'failed' ? 'Falló' : 'En proceso'}</StatusBadge>
              <DeliveryStatusBadge channel="Notion" status={r.notionStatus} />
              <DeliveryStatusBadge channel="Slack" status={r.slackStatus} />
            </span>
            {r.error ? <span className="text-meta text-danger">{r.error}</span> : null}
            {r.notionStatus === 'failed' && r.notionDetail ? <span className="text-meta text-danger">Notion: {r.notionDetail}</span> : null}
            {r.slackStatus === 'failed' && r.slackDetail ? <span className="text-meta text-danger">Slack: {r.slackDetail}</span> : null}
            {r.notionUrl ? (
              <a href={r.notionUrl} target="_blank" rel="noreferrer" className="text-meta text-brand-primary underline underline-offset-2">
                Abrir en Notion
              </a>
            ) : null}
          </div>
          <Button type="button" variant="secondary" size="sm" disabled={r.status !== 'generated'} onClick={() => onOpen(r)}>
            Ver reporte
          </Button>
        </li>
      ))}
    </ul>
  );
}

export interface LearningProposalCardProps {
  proposal: LearningProposal;
  onApprove?: (role: ApprovalRole) => void;
  onReject?: () => void;
  onImplement?: () => void;
  busy?: boolean;
  className?: string;
}

/** One supervised-learning proposal: evidence, both approvals, and the draft it became (never published here). */
export function LearningProposalCard({ proposal: p, onApprove, onReject, onImplement, busy, className }: LearningProposalCardProps) {
  const open = p.status === 'proposed';
  const approval = (role: ApprovalRole) => {
    const a = role === 'commercial' ? p.commercial : p.technical;
    return (
      <li key={role}>
        {APPROVAL_ROLE_LABELS[role]}: {a ? `${a.name} · ${fmtDate(a.at)}${a.note ? ` — ${a.note}` : ''}` : 'pendiente'}
      </li>
    );
  };
  return (
    <article className={cn('grid gap-3 rounded-card border border-border-subtle bg-surface-default p-4', className)} aria-label={p.title}>
      <header className="flex flex-wrap items-center gap-2">
        <StatusBadge tone="neutral">{LEARNING_KIND_LABELS[p.kind]}</StatusBadge>
        <StatusBadge tone={LEARNING_TONE[p.status]}>{LEARNING_STATUS_LABELS[p.status]}</StatusBadge>
        <span className="text-meta text-text-secondary">
          {[p.workspaceName, p.projectName].filter(Boolean).join(' · ') || 'Plataforma'} · semana del {p.weekStart}
        </span>
      </header>
      <div className="grid gap-1">
        <h4 className="text-body-lg font-semibold text-text-primary">{p.title}</h4>
        <p className="text-body text-text-secondary">{p.summary}</p>
        <p className="text-body text-text-primary">
          <span className="font-medium">Propuesta: </span>
          {p.suggestion}
        </p>
        <p className="text-meta text-text-secondary">Destino sugerido: {LEARNING_TARGET_LABELS[p.suggestedTarget]}</p>
      </div>
      {p.evidence.length ? (
        <div className="grid gap-1">
          <span className="text-meta font-medium text-text-secondary">Evidencia ({p.occurrences} casos; se muestran hasta 5, sin datos personales)</span>
          <ul className="grid gap-1">
            {p.evidence.map((e) => (
              <li key={`${e.kind}:${e.id}`} className="text-meta">
                <a href={e.link} className="text-brand-primary underline underline-offset-2">
                  {e.excerpt ?? `${e.kind === 'appointment' ? 'Cita' : e.kind === 'opportunity' ? 'Oportunidad' : e.kind === 'call' ? 'Llamada' : 'Conversación'} ${fmtDate(e.at)}`}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ul className="grid gap-0.5 text-meta text-text-secondary">
        {approval('commercial')}
        {approval('technical')}
        {p.rejection ? (
          <li className="text-danger">
            Rechazada por {p.rejection.name} · {fmtDate(p.rejection.at)}: {p.rejection.reason}
          </li>
        ) : null}
        {p.implemented ? (
          <li className="text-text-primary">
            {LEARNING_TARGET_LABELS[p.implemented.target]}: {p.implemented.note} · {p.implemented.by} · {fmtDate(p.implemented.at)}
            {p.implemented.testResult ? ` · Prueba: ${p.implemented.testResult}` : ''}
          </li>
        ) : null}
      </ul>
      <footer className="flex flex-wrap gap-2">
        {open && !p.commercial && onApprove ? (
          <Button type="button" size="sm" disabled={busy} onClick={() => onApprove('commercial')}>
            Aprobar (comercial)
          </Button>
        ) : null}
        {open && !p.technical && onApprove ? (
          <Button type="button" size="sm" disabled={busy} onClick={() => onApprove('technical')}>
            Aprobar (técnica)
          </Button>
        ) : null}
        {p.status === 'approved' && onImplement ? (
          <Button type="button" size="sm" disabled={busy} onClick={onImplement}>
            Implementar como borrador
          </Button>
        ) : null}
        {(open || p.status === 'approved') && onReject ? (
          <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={onReject}>
            Rechazar
          </Button>
        ) : null}
      </footer>
    </article>
  );
}
